import type { Worker, WorkshopHoliday, WorkPlanItem } from "./types.js";

export type WorkSegment = { startAt: string; endAt: string };
const dayMs = 86400000, offset = 9 * 3600000;
const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
/** A split keeps the real event timestamps; count that original clock only once. */
export function recordedWorkMinutes(items: Pick<WorkPlanItem, "id" | "splitOriginId" | "workStartedAt" | "workCompletedAt">[]): number {
  const groups = new Map<string, number[][]>();
  for (const item of items) {
    if (!item.workStartedAt || !item.workCompletedAt) continue;
    const start = Date.parse(item.workStartedAt), end = Date.parse(item.workCompletedAt);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) continue;
    const key = item.splitOriginId ?? item.id;
    groups.set(key, [...(groups.get(key) ?? []), [start, end]]);
  }
  let total = 0;
  for (const ranges of groups.values()) {
    let cursor = -Infinity;
    for (const [start, end] of ranges.sort((a, b) => a[0] - b[0])) { total += Math.max(0, end - Math.max(start, cursor)); cursor = Math.max(cursor, end); }
  }
  return total / 60000;
}
export function workingSegments(worker: Pick<Worker, "workDays" | "weekdayMinutes" | "weekendMinutes" | "workStartMinutes">, holidays: WorkshopHoliday[], start: number, end: number): WorkSegment[] {
  if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start || end - start > 181 * dayMs) return [];
  const segments: WorkSegment[] = [];
  const firstDay = Math.floor((start + offset) / dayMs) * dayMs - offset;
  for (let day = firstDay; day < end; day += dayMs) {
    const local = new Date(day + offset), weekday = local.getUTCDay(), date = local.toISOString().slice(0, 10);
    if (!worker.workDays.includes(weekdays[weekday])) continue;
    const minutes = Math.min(900, Math.max(0, [0, 6].includes(weekday) ? worker.weekendMinutes : worker.weekdayMinutes));
    const shiftStart = worker.workStartMinutes ?? 540;
    let windows = [[Math.max(start, day + shiftStart * 60000), Math.min(end, day + Math.min(1440, shiftStart + minutes) * 60000)]];
    for (const holiday of holidays.filter(holiday => holiday.dates.includes(date))) {
      const a = day + Number(holiday.startTime.slice(0, 2)) * 3600000, b = day + Number(holiday.endTime.slice(0, 2)) * 3600000;
      windows = windows.flatMap(([from, to]) => b <= from || a >= to ? [[from, to]] : [[from, Math.min(to, a)], [Math.max(from, b), to]]);
    }
    segments.push(...windows.filter(([a, b]) => b > a).map(([a, b]) => ({ startAt: new Date(a).toISOString(), endAt: new Date(b).toISOString() })));
  }
  return segments;
}
export const segmentMinutes = (segments: WorkSegment[]) => segments.reduce((sum, segment) => sum + (Date.parse(segment.endAt) - Date.parse(segment.startAt)) / 60000, 0);
export function divideWorkSegments(segments: WorkSegment[], percentages: number[]): WorkSegment[][] {
  const total = segments.reduce((sum, segment) => sum + Date.parse(segment.endAt) - Date.parse(segment.startAt), 0);
  let used = 0; let cursor = 0; let at = segments.length ? Date.parse(segments[0].startAt) : 0;
  return percentages.map((percent, index) => {
    let remaining = index === percentages.length - 1 ? total - used : Math.floor(total * percent / 100); used += remaining;
    const result: WorkSegment[] = [];
    while (remaining > 0 && cursor < segments.length) {
      const end = Date.parse(segments[cursor].endAt), amount = Math.min(end - at, remaining);
      if (amount > 0) result.push({ startAt: new Date(at).toISOString(), endAt: new Date(at + amount).toISOString() });
      at += amount; remaining -= amount;
      if (at >= end) { cursor++; if (segments[cursor]) at = Date.parse(segments[cursor].startAt); }
    }
    return result;
  });
}
export function finishWorkingMinutes(worker: Worker, holidays: WorkshopHoliday[], start: number, minutes: number): { end: number; segments: WorkSegment[] } | null {
  if (!Number.isFinite(minutes) || minutes <= 0) return null;
  const windows = workingSegments(worker, holidays, start, start + 180 * dayMs);
  if (!windows.length || Date.parse(windows[0].startAt) !== start) return null;
  const segments: WorkSegment[] = []; let remaining = minutes * 60000;
  for (const window of windows) {
    const a = Date.parse(window.startAt), b = Date.parse(window.endAt), used = Math.min(remaining, b - a);
    segments.push({ startAt: window.startAt, endAt: new Date(a + used).toISOString() }); remaining -= used;
    if (remaining <= 0) return { end: a + used, segments };
  }
  return null;
}
